// Analysis for the Snapchat Subpoena Processor: the IP kit for one account
// (likely home/work networks, VPN/Tor screening, day/night counts) and what
// the subpoenaed accounts share with each other (IPs, networks, emails, phones).

import React, { useState, useMemo } from 'react';
import { AnalysisTheme, LIGHT, useT, useTimeFormat, TIME_ZONES, Heading, Explain, DataTable, GeneratedBadge, selectStyle } from './ui';
import { IpAnalysis, LinksView } from './views';
import { parseTime, isIp, ipProfile, sharedLinks, phoneKey } from '../../utils/analysisKit';

const nameOf = a => a.info.username || a.folderName;

// One account's dated IP log (plus its creation IP) as analysis events.
export function subpoenaEvents(acct) {
  const account = nameOf(acct);
  const rows = acct.csvParsed?.ip_data?.sections?.[0]?.rows || [];
  const out = rows.map(r => {
    const p = parseTime(r.timestamp, 'UTC');
    return p && { t: p.t, account, kind: 'ip', source: `IP log${r.source ? ` (${r.source})` : ''}`, activity: true, ips: isIp(r.ip) ? [r.ip] : [], detail: r.user_agent || '' };
  }).filter(Boolean);
  const created = parseTime(acct.info.created, 'UTC');
  if (created && isIp(acct.info.creationIp)) out.push({ t: created.t, account, kind: 'created', source: 'Account created', activity: false, ips: [acct.info.creationIp], detail: 'Creation IP' });
  return out.sort((a, b) => a.t - b.t);
}

export default function SubpoenaAnalysis(props) {
  return (
    <AnalysisTheme.Provider value={LIGHT}>
      <Inner {...props} />
    </AnalysisTheme.Provider>
  );
}

function Inner({ view, accounts, account }) {
  const t = useT();
  const [tz, setTz] = useState('local');
  const fmt = useTimeFormat(tz);
  const tzLabel = tz === 'local' ? 'your time zone' : (TIME_ZONES.find(z => z[0] === tz) || [tz, tz])[1];
  const events = useMemo(() => (account ? subpoenaEvents(account) : []), [account]);
  const allEvents = useMemo(() => accounts.flatMap(subpoenaEvents), [accounts]);

  const { links, shared } = useMemo(() => {
    const prof = ipProfile(allEvents, 'UTC');
    const ident = new Map();
    for (const a of accounts) {
      const add = (label, v) => { if (!v) return; if (!ident.has(label)) ident.set(label, new Set()); ident.get(label).add(nameOf(a)); };
      String(a.info.email || '').split(/[;,\s]+/).filter(s => s.includes('@')).forEach(e => add(`email ${e.toLowerCase()}`, e));
      String(a.info.phone || '').split(/[;,]+/).map(phoneKey).filter(d => d.length >= 7).forEach(d => add(`phone ${d}`, d));
    }
    const nets = new Map();
    prof.filter(x => x.network).forEach(x => nets.set(x.network, [...new Set([...(nets.get(x.network) || []), ...x.accounts])]));
    const typed = {
      ips: prof.map(x => ({ label: x.ip, accounts: x.accounts })),
      networks: [...nets.entries()].map(([label, accts]) => ({ label, accounts: accts })),
      identifiers: [...ident.entries()].map(([label, accts]) => ({ label, accounts: [...accts] })),
    };
    return {
      links: sharedLinks(accounts.map(nameOf), typed),
      shared: [
        ...prof.filter(x => x.accounts.length > 1).map(x => ({ what: 'IP address', value: x.ip, accounts: x.accounts, count: x.count, first: x.first, last: x.last })),
        ...typed.networks.filter(n => n.accounts.length > 1).map(n => ({ what: 'IPv6 network', value: n.label, accounts: n.accounts })),
        ...typed.identifiers.filter(n => n.accounts.length > 1).map(n => ({ what: n.label.split(' ')[0] === 'email' ? 'Email' : 'Phone', value: n.label.replace(/^\w+ /, ''), accounts: n.accounts })),
      ],
    };
  }, [accounts, allEvents]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, color: t.text }}>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: 12.5, color: t.muted }}>
        <label style={{ marginLeft: 'auto' }}>Show times in{' '}
          <select value={tz} onChange={e => setTz(e.target.value)} style={selectStyle(t)}>
            {TIME_ZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      {view === 'ips' && account && (
        <>
          <Heading title={`IP analysis: ${nameOf(account)}`} sub="Likely home and work networks, day and night use of each IP, and optional VPN / Tor screening." />
          <GeneratedBadge />
          <Explain>
            Snap logs the IP address of each login and session. The address used on the most different nights is likely the
            home connection, and the one used most on weekdays 09:00&ndash;17:00 likely work or school. Screening shows which
            addresses are VPNs, Tor or hosting providers, whose location says nothing about where the person was. Send the
            ISP of a likely home IP your next legal process.
          </Explain>
          <IpAnalysis events={events} tz={tz} fmt={fmt} tzLabel={tzLabel} exportName={`${nameOf(account)} IP analysis`} />
        </>
      )}
      {view === 'links' && (
        <>
          <LinksView links={links} targetCount={accounts.length} what="subpoenaed accounts"
            explain="Here a shared email address or phone number means both accounts list it in their subscriber information." />
          {shared.length > 0 && (
            <>
              <Heading generated title="Everything shared" sub="Each IP address, IPv6 network, email or phone that appears in more than one account." />
              <DataTable key={tz} rows={shared} exportName="shared between accounts" initialSort={{ key: 'n', dir: 'desc' }}
                columns={[
                  { key: 'what', label: 'Type' },
                  { key: 'value', label: 'Value', mono: true, color: t.cyan },
                  { key: 'n', label: 'Accounts', text: r => String(r.accounts.length), sort: r => r.accounts.length },
                  { key: 'accounts', label: 'Which', text: r => r.accounts.join(', ') },
                  { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => (r.first ? fmt(r.first) : ''), sort: r => r.first || 0 },
                  { key: 'last', label: 'Last', mono: true, text: r => (r.last ? fmt(r.last) : ''), sort: r => r.last || 0 },
                ]} />
            </>
          )}
        </>
      )}
    </div>
  );
}
